import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const Portfolio = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      title: "Samarthya Events Website",
      description: "Event business website with modern design and booking system",
      category: "web",
      tags: ["React", "Laravel", "MySQL"],
      link: "#"
    },
    {
      title: "Samarthya Event Management App",
      description: "Event automation app with real-time updates and notifications",
      category: "mobile",
      tags: ["Flutter", "Firebase", "Cloud Functions"],
      link: "#"
    },
    {
      title: "Udaan BVM",
      description: "Fest website portal with registration and event management",
      category: "web",
      tags: ["PHP", "JavaScript", "Bootstrap"],
      link: "#"
    },
    {
      title: "Genesis BVM",
      description: "Event registration system with payment integration",
      category: "web",
      tags: ["Laravel", "MySQL", "Payment Gateway"],
      link: "#"
    },
    {
      title: "IEEE BVM",
      description: "Official student branch website with member portal",
      category: "web",
      tags: ["React", "Node.js", "MongoDB"],
      link: "#"
    },
    {
      title: "NSFT Website",
      description: "IEEE conference web experience with registration system",
      category: "web",
      tags: ["React", "Firebase", "Tailwind"],
      link: "#"
    },
    {
      title: "Attendance Hub",
      description: "Flutter attendance system app with QR code scanning",
      category: "mobile",
      tags: ["Flutter", "Firebase", "QR Scanner"],
      link: "#"
    },
    {
      title: "Complaint Management System",
      description: "Used at AMUL for tracking and resolving complaints",
      category: "web",
      tags: ["Laravel", "MySQL", "Admin Panel"],
      link: "#"
    }
  ];

  const categories = [
    { label: 'All Projects', value: 'all' },
    { label: 'Web', value: 'web' },
    { label: 'Mobile', value: 'mobile' }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="portfolio" className="section-padding section-dark">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-header">
            <h2 className="section-title">Featured Projects</h2>
            <div className="section-divider"></div>
            <p className="section-description">
              A showcase of my work and client projects
            </p>
          </div>

          <motion.div 
            className="portfolio-filters"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {categories.map((cat) => (
              <motion.button
                key={cat.value}
                className={`filter-btn ${filter === cat.value ? 'active' : ''}`}
                onClick={() => setFilter(cat.value)}
                whileHover={{ 
                  scale: 1.1,
                  y: -2,
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
                whileTap={{ scale: 0.95 }}
                animate={{
                  scale: filter === cat.value ? 1.05 : 1,
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                {cat.label}
              </motion.button>
            ))}
          </motion.div>

          <motion.div
            className="portfolio-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            layout
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={index}
                className="portfolio-card"
                variants={cardVariants}
                layout
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: index * 0.1
                }}
                whileHover={{ 
                  y: -15,
                  scale: 1.05,
                  rotateX: 5,
                  rotateY: 5,
                  boxShadow: "0 20px 40px rgba(99, 102, 241, 0.3)",
                  transition: { type: "spring", stiffness: 300, damping: 20 }
                }}
              >
                <motion.div 
                  className="project-icon"
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    delay: index * 0.3
                  }}
                >
                  {project.category === 'mobile' ? '📱' : '🌐'}
                </motion.div>
                <motion.h3 
                  className="project-title"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                >
                  {project.title}
                </motion.h3>
                <motion.p 
                  className="project-description"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                >
                  {project.description}
                </motion.p>
                <div className="project-tags">
                  {project.tags.map((tag, i) => (
                    <motion.span 
                      key={i} 
                      className="project-tag"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ 
                        delay: index * 0.1 + 0.4 + i * 0.05,
                        type: "spring",
                        stiffness: 200
                      }}
                      whileHover={{
                        scale: 1.15,
                        y: -2,
                        transition: { duration: 0.2 }
                      }}
                    >
                      {tag}
                    </motion.span>
                  ))}
                </div>
                <div className="project-links">
                  <motion.a
                    href={project.link}
                    className="project-link"
                    whileHover={{ 
                      scale: 1.2,
                      rotate: 360,
                      transition: { duration: 0.5 }
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {/* <FaExternalLinkAlt /> View Project */}
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
